import { SupportedTimeZones } from 'enums';
import { DAY_IN_HOURS, HOUR_IN_MINUTES } from 'consts';
import spacetime, { Spacetime, TimeUnit } from 'spacetime';

import { timeZoneMappingService } from './TimeZoneMappingService';

import type { TimeZoneSelectOption, WorkingDayAmPmRange } from 'types';

export const getTimeZoneSummary = (timeZoneName: SupportedTimeZones) => {
  if (!timeZoneName) return '';

  const timeZoneData =
    timeZoneMappingService.mapCorrectTimeZoneDataToDeprecated(timeZoneName);
  const { name: countryName } =
    timeZoneMappingService.getCorrectCountryDataByTimeZone(timeZoneName);
  const timeZoneOffset = timeZoneData.offset();

  const softTzNameRaw = timeZoneMappingService.getSoftTimeZoneName(
    timeZoneData.timezone()
  );

  const GMTOffsetText = `(GMT${timeZoneOffset > 0 ? '+' : '-'}${
    Math.abs(timeZoneOffset) / HOUR_IN_MINUTES
  })`;

  const countryText = `${countryName}`;

  const softTzNameText = `${
    softTzNameRaw === timeZoneName || !softTzNameRaw ? '' : softTzNameRaw
  }`;

  const IANATzNameText = `${timeZoneName}`;

  return `${GMTOffsetText} ${softTzNameText} - ${IANATzNameText}, ${countryText}`;
};

export const getTimeZoneGTMWithCountry = (timeZoneName: SupportedTimeZones) => {
  if (!timeZoneName) return '';

  const timeZoneData =
    timeZoneMappingService.mapCorrectTimeZoneDataToDeprecated(timeZoneName);
  const { name: countryName } =
    timeZoneMappingService.getCorrectCountryDataByTimeZone(timeZoneName);
  const timeZoneOffset = timeZoneData.offset();

  const GMTOffsetText = `GMT${timeZoneOffset > 0 ? '+' : '-'}${
    Math.abs(timeZoneOffset) / HOUR_IN_MINUTES
  }`;

  const countryText = `${countryName}`;

  return `${GMTOffsetText}, ${countryText}`;
};

export const getReadableTimeZoneDateTime = (timeZoneData: Spacetime) =>
  timeZoneData.format('nice');

export const getTimeZoneDataByOffsetInMinutes = (offset: number) => {
  return spacetime().goto(String(offset / 60));
};

export const createTimeZoneOption = (
  timeZoneName: SupportedTimeZones
): TimeZoneSelectOption => {
  return {
    id: timeZoneName,
    label: getTimeZoneSummary(timeZoneName),
    tzOffset: timeZoneMappingService
      .mapCorrectTimeZoneDataToDeprecated(timeZoneName)
      .offset(),
    searchContextHelper: timeZoneMappingService
      .getSoftTzAbbreviations(timeZoneName)
      .filter((el) => !el.includes('UTC'))
      .join(' '),
  };
};

export const calculateTimeDifference = ({
  workingDayStartAmPm,
  workingDayEndAmPm,
  unit = 'minute',
}: {
  workingDayStartAmPm: WorkingDayAmPmRange;
  workingDayEndAmPm: WorkingDayAmPmRange;
  unit?: TimeUnit;
}) => {
  const isAmPmSame = workingDayStartAmPm.ampm === workingDayEndAmPm.ampm;
  const isStartEqualEnd = workingDayStartAmPm.hours === workingDayEndAmPm.hours;

  if (isAmPmSame && isStartEqualEnd) return DAY_IN_HOURS * HOUR_IN_MINUTES;

  const workingDayStartHour = spacetime().time(
    workingDayStartAmPm.hours + workingDayStartAmPm.ampm
  );

  const workingDayEndHour = workingDayStartHour.time(
    workingDayEndAmPm.hours + workingDayEndAmPm.ampm,
    true
  );

  return workingDayStartHour.diff(workingDayEndHour, unit);
};
